import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Cpu } from 'lucide-react';
import { View } from '../../types.ts';
import AdminSidebar from './components/AdminSidebar.tsx';
import AdminTopbar from './components/AdminTopbar.tsx';
import PlatformAdminDashboard from './views/PlatformAdminDashboard.tsx';
import TenantsAdminView from './views/TenantsAdminView.tsx';
import CompaniesAdminView from './views/CompaniesAdminView.tsx';
import UsersAdminView from './views/UsersAdminView.tsx';
import FinanceAdminView from './views/FinanceAdminView.tsx';
import { PlansAdminView } from './views/PlansAdminView.tsx';
import { BillingAdminView } from './views/BillingAdminView.tsx';
import GlobalSettingsAdminView from './views/GlobalSettingsAdminView.tsx';
import AdminBESView from './views/AdminBESView.tsx';
import AIControlCenterView from './views/AIControlCenterView.tsx';
import SystemSettingsAdminView from './views/SystemSettingsAdminView.tsx';
import IntegrationsAdminView from './views/IntegrationsAdminView.tsx';
import { useAuth } from '../../context/AuthContext.tsx';
import WelcomeModal from '../../components/layout/WelcomeModal.tsx';
import apiClient from '../../api/client';

interface AdminLayoutProps {
  currentView: View | string;
  setCurrentView: (view: View) => void;
}

export default function AdminLayout({ currentView, setCurrentView }: AdminLayoutProps) {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const { fetchWithAuth, user, dbUser, loading } = useAuth();
  const [metrics, setMetrics] = useState<any>(null);
  const [loadingMetrics, setLoadingMetrics] = useState(true);
  const [metricsError, setMetricsError] = useState<string | null>(null);
  const [showWelcome, setShowWelcome] = useState(false);

  const roleHints = useMemo(() => {
    let storedRoles: string[] = [];
    try {
      const raw = localStorage.getItem('cyzor_roles');
      storedRoles = raw ? JSON.parse(raw) : [];
    } catch {
      storedRoles = [];
    }

    const fromDb = Array.isArray(dbUser?.roles) ? dbUser.roles : [];
    const fromUser = Array.isArray((user as any)?.roles) ? (user as any).roles : [];
    return [...storedRoles, ...fromDb, ...fromUser].filter(Boolean);
  }, [dbUser, user]);

  const isPlatformAdmin = useMemo(
    () => Boolean(
      dbUser?.isPlatformAdmin === true ||
      roleHints.some((role) => ['SUPER_ADMIN', 'ADMIN', 'PLATFORM_ADMIN'].includes(String(role).toUpperCase()))
    ),
    [dbUser, roleHints]
  );

  useEffect(() => {
    // Show welcome modal after login (once per session)
    if (user && !loading && !sessionStorage.getItem('welcome_modal_shown')) {
      const timer = setTimeout(() => {
        setShowWelcome(true);
        sessionStorage.setItem('welcome_modal_shown', 'true');
      }, 1500); // Delay slightly for smoother experience
      return () => clearTimeout(timer);
    }
  }, [user, loading]);

  useEffect(() => {
    if (!loading && !user) {
      setCurrentView('login');
      return;
    }

    if (!loading && user && !isPlatformAdmin) {
      setCurrentView('dashboard');
    }
  }, [user, dbUser, loading, isPlatformAdmin, setCurrentView]);

  const loadMetrics = useCallback(async () => {
    if (!isPlatformAdmin) return;
    try {
      setLoadingMetrics(true);
      setMetricsError(null);
      const res = await apiClient.get('/api/admin/dashboard/metrics');
      const payload = res.data?.data ?? res.data;
      setMetrics(payload?.metrics || payload);
    } catch (error: any) {
      console.error("Failed to load admin metrics", error);
      setMetricsError(error?.message || 'Erro ao carregar métricas do painel administrativo.');
    } finally {
      setLoadingMetrics(false);
    }
  }, [isPlatformAdmin]);

  useEffect(() => {
    if (isPlatformAdmin) {
      loadMetrics();
    }
  }, [loadMetrics, isPlatformAdmin]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">Carregando admin...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Sessão expirada</h1>
          <p className="mt-2 text-gray-600">Faça login novamente para acessar o painel administrativo.</p>
        </div>
      </div>
    );
  }

  if (!isPlatformAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Acesso restrito</h1>
          <p className="mt-2 text-gray-600">Você não tem permissão para acessar este painel.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-[#F7F8F9] text-[#0F0F10] selection:bg-[#0F0F10] selection:text-white font-sans relative overflow-hidden">
      <AdminSidebar
        isCollapsed={isSidebarCollapsed}
        toggleSidebar={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        currentView={currentView}
        setCurrentView={setCurrentView}
      />
      
      <div className={`transition-all duration-300 flex-1 flex flex-col min-w-0 h-screen overflow-hidden relative z-10`}>
        <AdminTopbar 
          isSidebarCollapsed={isSidebarCollapsed} 
          toggleSidebar={() => setIsSidebarCollapsed(!isSidebarCollapsed)} 
          setCurrentView={setCurrentView}
        />
        
        <main className="flex-1 overflow-y-auto bg-transparent px-4 md:px-8 pb-16 w-full scrollbar-thin scrollbar-thumb-gray-200">
          {metricsError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              <p className="font-bold">Erro ao carregar dados do painel</p>
              <p>{metricsError}</p>
              <button
                onClick={loadMetrics}
                className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg text-xs font-bold hover:bg-red-700 transition"
              >
                Tentar novamente
              </button>
            </div>
          )}
          {currentView === 'admin' && (
            <PlatformAdminDashboard 
              metrics={metrics} 
              loading={loadingMetrics} 
              onRefresh={loadMetrics} 
            />
          )}
          {currentView === 'admin-tenants' && <TenantsAdminView />}
          {currentView === 'admin-companies' && <CompaniesAdminView />}
          {currentView === 'admin-users' && <UsersAdminView />}
          {currentView === 'admin-finance' && <FinanceAdminView />}
          {currentView === 'admin-plans' && <PlansAdminView />}
          {currentView === 'admin-billing' && <BillingAdminView />}
          {currentView === 'admin-settings' && <GlobalSettingsAdminView />}
          {currentView === 'admin-bes' && <AdminBESView />}
          {currentView === 'admin-ai-control-center' && <AIControlCenterView />}
          {currentView === 'admin-infrastructure' && <SystemSettingsAdminView />}

          {currentView === 'admin-integrations' && <IntegrationsAdminView />}
        </main>
      </div>

      <WelcomeModal 
        isOpen={showWelcome} 
        onClose={() => setShowWelcome(false)} 
        onUpgrade={() => {
          setShowWelcome(false);
          setCurrentView('admin-plans');
        }} 
      />
    </div>
  );
}
